import { formatCount } from './runtimeLimitsForm.js';

export const USAGE_RANGES = [
  { days: 1, label: '1 day', column: 'Today', field: 'today', summary: 'today' },
  { days: 7, label: '7 days', column: '7 days', field: 'last7', summary: 'in the last 7 days' },
  { days: 30, label: '30 days', column: '30 days', field: 'last30', summary: 'in the last 30 days' },
];

export const DEFAULT_RANGE_DAYS = 7;

// Projected monthly costs above this show red; it matches the Fireworks monthly spend cap.
const HIGH_COST_DOLLARS = 25;

const SERIES_COLORS = ['#2563eb', '#d97706', '#7c3aed', '#059669', '#db2777', '#0891b2', '#65a30d', '#dc2626'];

const SHORT_DATE = { timeZone: 'UTC', month: 'short', day: 'numeric' };
const LONG_DATE = { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' };

function parseIsoDate(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

function formatIsoDate(isoDate, options) {
  const date = parseIsoDate(isoDate);
  return date ? date.toLocaleDateString('en-US', options) : isoDate || '—';
}

export function formatShortDate(isoDate) {
  return formatIsoDate(isoDate, SHORT_DATE);
}

export function formatLongDate(isoDate) {
  return formatIsoDate(isoDate, LONG_DATE);
}

export function formatDollars(value) {
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** A price per 1,000 calls: whole dollars without cents, otherwise to the cent. */
export function formatRate(dollars) {
  const digits = Number.isInteger(dollars) ? 0 : 2;
  return Number(dollars).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function isGooglePriced(line) {
  return line.freePerMonth !== null && line.freePerMonth !== undefined;
}

export function freeAllowanceText(line) {
  return `${formatCount(line.freePerMonth)} free a month, then ${formatRate(line.per1000)} per 1,000`;
}

export function freeUsedText(line) {
  return `${formatCount(line.monthToDate)} of ${formatCount(line.freePerMonth)} free calls used so far. On pace for ${formatCount(line.projectedMonth)} this month.`;
}

export function costCellText(line) {
  return line.estimatedCostMonthToDate === null ? 'Billed by tokens' : formatDollars(line.estimatedCostMonthToDate);
}

/** Width of a progress bar for a percent that can pass 100. */
export function barWidthPercent(percent) {
  return Math.min(100, Math.max(0, Number(percent) || 0));
}

/** 'high_cost' above HIGH_COST_DOLLARS projected; 'over_free' when the month is projected past the free allowance. */
export function allowanceTone(line) {
  if (line.projectedCostMonth > HIGH_COST_DOLLARS) return 'high_cost';
  if (line.projectedMonth > line.freePerMonth) return 'over_free';
  return 'within_free';
}

/** The same scale for a dollar total: any cost at all means some free allowance ran out. */
export function costTone(dollars) {
  if (dollars > HIGH_COST_DOLLARS) return 'high_cost';
  if (dollars > 0) return 'over_free';
  return 'within_free';
}

export function rangeFor(days) {
  return USAGE_RANGES.find((range) => range.days === days) ?? USAGE_RANGES[0];
}

export function rangeCalls(report, days) {
  const { field } = rangeFor(days);
  return report.byKey.reduce((sum, line) => sum + line[field], 0);
}

/**
 * What the usage chart draws. The 1-day view is one bar per limit for today;
 * longer ranges are one stacked bar per day. Series follow the API's order,
 * so colors stay stable across ranges.
 */
export function chartModel(report, days) {
  const series = report.byKey.map((line, index) => ({
    key: line.key,
    label: line.label,
    color: SERIES_COLORS[index % SERIES_COLORS.length],
  }));

  if (days === 1) {
    return {
      layout: 'by_limit',
      series,
      rows: report.byKey.map((line, index) => ({
        key: line.key,
        label: line.label,
        calls: line.today,
        color: series[index].color,
      })),
    };
  }

  return {
    layout: 'by_day',
    series,
    rows: report.days.slice(-days).map((day) => ({
      date: day.date,
      label: formatShortDate(day.date),
      counts: day.counts,
    })),
  };
}
